import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import SystemDataService from '../../Services/SystemDataService';
import MediaComponent from '../Media/media-component';

const SPEED_PX = 1.2; // auto-scroll speed (px per frame)

export default function InfoBarSection({ cards: propCards, alwaysScroll = false, title, seeAllText, seeAllHref = '/services' }) {
    const systemData = useSelector((state) => state.systemData || {});
    const { dir } = systemData;

    const [cards, setCards] = useState([]);
    const [copies, setCopies] = useState(2); // grown until 2 identical halves each fill the viewport

    const containerRef = useRef(null);
    const rafRef = useRef(null);
    const hoverRef = useRef(false);
    const accRef = useRef(0); // carries the sub-pixel scroll remainder between frames
    const dragRef = useRef({ active: false, startX: 0, startScroll: 0 });

    useEffect(() => {
        if (propCards?.length) {
            setCards(propCards);
        } else {
            SystemDataService.getCardInfo().then((data) => setCards(data || []));
        }
    }, [propCards]);

    // Render enough copies that one "half" of the track is at least as wide as
    // the viewport, otherwise the seamless wrap point can't be reached and the
    // strip appears to stop.
    useEffect(() => {
        const measure = () => {
            const c = containerRef.current;
            if (!c || !cards.length) return;
            const setWidth = c.scrollWidth / copies;
            if (!setWidth) return;
            const halvesNeeded = Math.max(1, Math.ceil(c.clientWidth / setWidth));
            const want = halvesNeeded * 2; // even → two identical halves
            if (want !== copies) setCopies(want);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [cards, copies]);

    // Auto-scroll + seamless wrap (works for the drag/native-scroll position too).
    useEffect(() => {
        const c = containerRef.current;
        if (!c || !cards.length) return;
        const step = () => {
            if (!dragRef.current.active && !hoverRef.current) {
                // Accumulate the fractional speed and only ever write whole pixels,                 // mobile browsers round scrollLeft to integers, so a bare `+= 0.7`
                // truncates to 0 every frame and the strip never moves.
                accRef.current += SPEED_PX;
                const whole = Math.trunc(accRef.current);
                if (whole) {
                    c.scrollLeft += whole;
                    accRef.current -= whole;
                }
            }
            const half = c.scrollWidth / 2;
            if (half > 0) {
                if (c.scrollLeft >= half) c.scrollLeft -= half;
                else if (c.scrollLeft <= 0) c.scrollLeft += half;
            }
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafRef.current);
    }, [cards, copies]);

    if (!cards.length) return null;

    const CardItem = ({ card, active }) => (
        <div className={`group flex flex-col items-center gap-4 flex-shrink-0 mx-1.5 my-1 py-6 px-6 md:px-10 rounded-[28px] transition-colors duration-200 cursor-default text-center ${active ? 'bg-primary-50' : 'hover:bg-primary-50'}`}>
            {card.image && (
                <div className="h-16 w-16 md:h-[76px] md:w-[76px] flex-shrink-0 flex items-center justify-center">
                    <MediaComponent
                        src={card.image}
                        alt={card.title}
                        objectFit="contain"
                        className="h-full w-auto"
                        wrapperClassName="h-full flex items-center"
                        useSekeleton={false}
                    />
                </div>
            )}
            <div className="flex flex-col leading-tight">
                <span className="text-base md:text-lg font-bold text-primary-900 whitespace-pre-line text-balance">
                    {card.title}
                </span>
            </div>
        </div>
    );

    // Mouse drag-to-scroll (touch uses native horizontal scrolling via pan-x).
    const onPointerDown = (e) => {
        if (e.pointerType !== 'mouse') return;
        const c = containerRef.current;
        dragRef.current = { active: true, startX: e.clientX, startScroll: c.scrollLeft };
        c.setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (!dragRef.current.active) return;
        const c = containerRef.current;
        c.scrollLeft = dragRef.current.startScroll - (e.clientX - dragRef.current.startX);
    };
    const endDrag = () => { dragRef.current.active = false; };

    return (
        <div dir={dir} className="relative w-full overflow-hidden bg-white pt-6 md:pt-10 pb-4 md:pb-6 px-4 md:px-6">

            {/* Section header, heading + "see all" link (rendered only when a title is given) */}
            {title && (
                <div className="relative z-10 mx-auto mb-5 md:mb-8 flex max-w-[1200px] items-center justify-between gap-4 px-1">
                    <h2 className="font-secondary text-2xl md:text-[32px] font-extrabold leading-tight text-primary-900">
                        {title}
                    </h2>
                    {seeAllText && (
                        <a href={seeAllHref} className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm md:text-base font-bold text-secondary-500 transition-colors hover:text-secondary-600">
                            {dir === 'rtl' && <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />}
                            {seeAllText}
                            {dir !== 'rtl' && <ChevronRight className="h-4 w-4" strokeWidth={2.5} />}
                        </a>
                    )}
                </div>
            )}

            {/* Desktop static row (only when not forced to always scroll) */}
            <div className={`${alwaysScroll ? 'hidden' : 'hidden md:flex'} relative z-10 max-w-[1200px] mx-auto flex-row flex-nowrap justify-center items-center w-full`}>
                {cards.map((card, index) => (
                    <CardItem key={card.id || index} card={card} active={index === 0} />
                ))}
            </div>

            {/* Endless marquee, auto-scrolls, loops seamlessly, and can be dragged.
                Contained to a max-width (not full screen) with soft edge fades. */}
            <div className={`${alwaysScroll ? 'block' : 'block md:hidden'} relative z-10 mx-auto w-full max-w-[1200px] overflow-hidden`}>
                {/* edge fades, match the section background */}
                <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 md:w-20 bg-gradient-to-r from-white to-transparent" />
                <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 md:w-20 bg-gradient-to-l from-white to-transparent" />
                <div
                    ref={containerRef}
                    dir="ltr"
                    className="flex w-full overflow-x-auto scrollbar-hide select-none cursor-grab active:cursor-grabbing"
                    style={{ touchAction: 'pan-x', overscrollBehaviorX: 'contain' }}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onPointerLeave={endDrag}
                    onMouseEnter={() => { hoverRef.current = true; }}
                    onMouseLeave={() => { hoverRef.current = false; }}
                >
                    <div className="flex flex-nowrap items-center w-max">
                        {Array.from({ length: copies }).flatMap((_, copyIdx) =>
                            cards.map((card, index) => (
                                <CardItem key={`${copyIdx}-${card.id || index}`} card={card} />
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
